import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { getEditFeatureRoute, SUPPORT_ADMIN_ROUTE } from "../../../../../constants/routes"
import React, { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { MenuItem, Switch } from "@mui/material"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import {
    changeFeatureStatus,
    fetchSupportFeatures,
    FetchSupportFeaturesResponse,
    SupportFeaturesQuery,
} from "../featuresApi"
import { SupportFeature } from "../featuresTypes"
import { FeatureStatusChip } from "../components/FeatureStatusChip"

function transformData(data: FetchSupportFeaturesResponse) {
    return data.features
}

export function SupportFeatures() {
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()

    const supportFeatures = useApiQuery(fetchSupportFeatures, {
        queryName: SupportFeaturesQuery.LIST,
        dependencies: {},
        select: transformData,
    })

    const { mutate: callChangeFeatureStatus, isLoading } = useApiMutation(changeFeatureStatus, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportFeaturesQuery.LIST] })
        },
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supportFeatures)

    const columns = useMemo<MRT_ColumnDef<SupportFeature>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("support-admin-features-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-admin-features-table-header-plan"),
                accessorKey: "plan",
                accessorFn: (row) => l10n.getStringForEnum("support-admin-features-table-plan", row.plan, "none"),
            }),

            columnFactory.statusColumn({
                header: l10n.getString("support-admin-features-table-header-status"),
                accessorKey: "status",
                Cell: ({ row }) => <FeatureStatusChip status={row.original.status} />,
                filterSelectOptions: [
                    {
                        value: "NEW",
                        text: l10n.getString("support-admin-features-status-new"),
                    },
                    {
                        value: "BETA",
                        text: l10n.getString("support-admin-features-status-beta"),
                    },
                ],
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-admin-features-table-header-enabled"),
                accessorKey: "isEnabled",
                Cell: ({ row }) => (
                    <Switch
                        disabled={isLoading}
                        checked={row.original.isEnabled}
                        onChange={() =>
                            callChangeFeatureStatus({
                                featureId: row.original.id,
                                isEnabled: !row.original.isEnabled,
                            })
                        }
                    />
                ),
            }),
        ],
        [l10n, columnFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<SupportFeature>; closeMenu: () => void }) => [
            <MenuItem
                key={`edit-${row.original.id}`}
                onClick={() => {
                    closeMenu()
                    navigate(getEditFeatureRoute(row.original.id))
                }}
            >
                <LocalizedStrict id="support-admin-features-table-action-edit">
                    <span>Edit</span>
                </LocalizedStrict>
            </MenuItem>,
        ],

        [navigate],
    )

    return (
        <>
            <LocalizedStrict id="support-admin-features-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_ADMIN_FEATURES}
                    title="Features"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-entity-header-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-features">
                            <Breadcrumb>Features</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
                enableExpanding
                getSubRows={(row) => row.children}
            />
        </>
    )
}

